import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { usePOS } from "@/context/POSContext";
import { listPrinters, uploadBillLogo } from "@/lib/printerApi";

/**
 * Controls for settings whose stored value is a string or number but whose MEANING
 * is a time, an hour, a printer, a schedule or an image (settings sweep X3). Each
 * writes exactly the value the old free-text box did, so nothing downstream moves.
 */

const pad = n => String(n).padStart(2, "0");
const clock = (h, m = 0) => {
  if (h === 0 && m === 0) return "Midnight";
  if (h === 12 && m === 0) return "Noon";
  const suffix = h < 12 ? "am" : "pm";
  return `${h % 12 || 12}:${pad(m)} ${suffix}`;
};

// Every 15 minutes, as "HH:MM", labelled the way the floor says it.
const QUARTERS = Array.from({ length: 96 }, (_, i) => {
  const h = Math.floor(i / 4), m = (i % 4) * 15;
  return { value: `${pad(h)}:${pad(m)}`, label: clock(h, m) };
});

function PickList({ value, options, onChange, disabled, label, testId, width = "w-48" }) {
  // A stored value outside the list (an odd time typed before this control) still
  // shows, rather than the control silently reading as something else.
  const shown = options.some(option => option.value === value) || value === "" || value == null
    ? options
    : [{ value, label: `${value} (current)` }, ...options];
  return (
    <Select value={value ?? ""} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className={`h-8 ${width} text-body`} aria-label={label} data-testid={testId}>
        <SelectValue placeholder="Choose…" />
      </SelectTrigger>
      <SelectContent className="max-h-72">
        {shown.map(option => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export function TimeOfDayControl({ meta, value, onChange, disabled }) {
  return <PickList value={String(value ?? "")} options={QUARTERS} onChange={onChange} disabled={disabled}
    label={meta.display_name} testId={`setting-${meta.key}-time`} />;
}


export function PrinterControl({ meta, value, onChange, disabled }) {
  const { token } = usePOS();
  const [printers, setPrinters] = useState(null);
  useEffect(() => {
    let live = true;
    listPrinters(token).then(list => { if (live) setPrinters(Array.isArray(list) ? list : []); }).catch(() => { if (live) setPrinters([]); });
    return () => { live = false; };
  }, [token]);
  const options = [
    { value: "0", label: "Automatic (first active printer)" },
    ...(printers || []).map(printer => ({ value: String(printer.id), label: `${printer.name}${printer.is_active === false ? " (inactive)" : ""}` })),
  ];
  return <PickList value={String(value ?? "0")} options={options} onChange={onChange} disabled={disabled || printers == null}
    label={meta.display_name} testId={`setting-${meta.key}-printer`} width="w-56" />;
}

export function AddressControl({ meta, value, onChange, disabled }) {
  return (
    <textarea
      className="min-h-[4.5rem] w-64 rounded-md border border-input bg-card px-2 py-1.5 text-body"
      value={value ?? ""}
      onChange={event => onChange(event.target.value)}
      disabled={disabled}
      aria-label={meta.display_name}
      placeholder={"Street, area\nCity, PIN"}
      data-testid={`setting-${meta.key}-textarea`}
    />
  );
}

/**
 * Report dayparts: stored as a JSON array of { name, from, to }. Editing the JSON
 * by hand broke the daypart report on one missing quote; this edits the same
 * array as rows.
 */
export function DaypartsControl({ meta, value, onChange, disabled }) {
  let rows = [];
  let unreadable = false;
  try { rows = Array.isArray(JSON.parse(value || "[]")) ? JSON.parse(value || "[]") : []; } catch { unreadable = true; }
  const write = next => onChange(JSON.stringify(next));
  const set = (index, field, fieldValue) => write(rows.map((row, i) => (i === index ? { ...row, [field]: fieldValue } : row)));

  if (unreadable) {
    return (
      <div className="w-72 space-y-1" data-testid={`setting-${meta.key}-unreadable`}>
        <p className="text-meta text-danger">The saved dayparts cannot be read, so the daypart report cannot use them.</p>
        {!disabled && (
          <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => write([])} data-testid="daypart-reset">
            Start Over (no dayparts)
          </Button>
        )}
      </div>
    );
  }
  return (
    <div className="w-[26rem] space-y-1" data-testid={`setting-${meta.key}-dayparts`}>
      {rows.map((row, index) => (
        <div key={index} className="grid grid-cols-[1fr_7.5rem_7.5rem_2rem] items-center gap-1">
          <Input className="h-8 text-body" value={row.name || ""} disabled={disabled} aria-label={`Daypart ${index + 1} name`}
            onChange={event => set(index, "name", event.target.value)} data-testid={`daypart-name-${index}`} />
          <PickList value={row.from || ""} options={QUARTERS} onChange={next => set(index, "from", next)} disabled={disabled} label={`Daypart ${index + 1} starts`} testId={`daypart-from-${index}`} width="w-full" />
          <PickList value={row.to || ""} options={QUARTERS} onChange={next => set(index, "to", next)} disabled={disabled} label={`Daypart ${index + 1} ends`} testId={`daypart-to-${index}`} width="w-full" />
          <button type="button" className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-danger"
            disabled={disabled} onClick={() => write(rows.filter((_, i) => i !== index))} aria-label={`Remove ${row.name || "daypart"}`} data-testid={`daypart-remove-${index}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      {!disabled && (
        <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => write([...rows, { name: "", from: "12:00", to: "16:00" }])} data-testid="daypart-add">
          <Plus className="h-4 w-4" />Add Daypart
        </Button>
      )}
    </div>
  );
}

/**
 * The bill logo is a rasterised bitmap; the row used to show that bitmap as an
 * editable text box. Upload and remove go through the same endpoint the Printers
 * module uses, which saves at once (it is not part of the staged batch).
 */
export function LogoControl({ meta, value, disabled }) {
  const { token } = usePOS();
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [hasLogo, setHasLogo] = useState(Boolean(value));
  useEffect(() => setHasLogo(Boolean(value)), [value]);

  const send = async b64 => {
    setBusy(true);
    try {
      await uploadBillLogo(b64, token);
      setHasLogo(Boolean(b64));
      toast.success(b64 ? "Bill logo saved — it prints above the header" : "Bill logo removed — bills print text only");
    } catch (failure) {
      toast.error(`Could not ${b64 ? "save" : "remove"} the logo: ${failure?.response?.data?.detail || failure?.message || "server unreachable"}`);
    } finally {
      setBusy(false);
    }
  };
  const onFile = event => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      // An empty or unreadable file has no payload, and "" is the server's "clear":
      // it would have wiped the logo and toasted "removed". Only Remove clears.
      const payload = String(reader.result || "").split(",")[1] || "";
      if (!payload) {
        toast.error("That file could not be read as an image — the logo was not changed");
        return;
      }
      send(payload);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="flex items-center gap-2" data-testid={`setting-${meta.key}-logo`}>
      <span className="text-meta text-muted-foreground" data-testid={`setting-${meta.key}-logo-state`}>{hasLogo ? "Logo set" : "No logo"}</span>
      <input ref={input} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`setting-${meta.key}-logo-file`} />
      <Button type="button" variant="outline" size="sm" className="h-8" disabled={disabled || busy} onClick={() => input.current?.click()}>
        <Upload className="h-4 w-4" />{busy ? "Saving…" : hasLogo ? "Replace" : "Upload"}
      </Button>
      {hasLogo && (
        <Button type="button" variant="ghost" size="sm" className="h-8" disabled={disabled || busy} onClick={() => send("")} data-testid={`setting-${meta.key}-logo-remove`}>
          Remove
        </Button>
      )}
    </div>
  );
}

/** Which keys get which control. Anything not listed keeps the generic control. */
export const CUSTOM_CONTROLS = {
  business_day_start: TimeOfDayControl,
  default_printer_id: PrinterControl,
  address: AddressControl,
  report_dayparts: DaypartsControl,
  bill_logo_bmp: LogoControl,
};
